import { resolveQuestionnaireDisplayName } from '@/lib/questionnaireDisplayName';
import type { FormsTemplateSummary } from '@/hooks/useFormsTemplates';

export type QuestionnairePickerItem = {
  id: string;
  name: string;
  detail: string;
  group: 'platform' | 'clinic';
  tags: string[];
  description: string | null;
  questionCount: number | null;
};

export function questionnairePickerName(
  template: Pick<FormsTemplateSummary, 'id' | 'display_name_da' | 'display_name_en'>,
  language: string,
  fallback: string,
): string {
  return resolveQuestionnaireDisplayName({
    templateId: template.id,
    snapshotName: language.startsWith('en') ? template.display_name_en : template.display_name_da,
    locale: language,
    templatesById: {
      [template.id]: {
        id: template.id,
        display_name_da: template.display_name_da ?? '',
        display_name_en: template.display_name_en ?? '',
      },
    },
    fallback,
  });
}

export function questionnairePickerDetail(
  template: Pick<FormsTemplateSummary, 'description' | 'question_count' | 'estimated_minutes'>,
  questionsLabel?: (count: number) => string,
): string {
  const parts: string[] = [];
  const description = template.description?.trim();
  if (description) parts.push(description);
  if (template.question_count && template.question_count > 0) {
    parts.push(questionsLabel ? questionsLabel(template.question_count) : String(template.question_count));
  }
  return parts.join(' · ');
}

export function toQuestionnairePickerItems(
  templates: readonly FormsTemplateSummary[],
  language: string,
  fallback: string,
  questionsLabel?: (count: number) => string,
): QuestionnairePickerItem[] {
  return templates.map((template) => ({
    id: template.id,
    name: questionnairePickerName(template, language, fallback),
    detail: questionnairePickerDetail(template, questionsLabel),
    group: template.is_platform ? 'platform' : 'clinic',
    tags: template.tags ?? [],
    description: template.description?.trim() || null,
    questionCount: template.question_count ?? null,
  }));
}

